import type { DsdEditorValueOccurrence } from "@/apis/dsd-editor/route";
import { AppPageShell } from "@/components/app-shell";
import { useDocumentSidebarToggle } from "@/components/document-sidebar";
import type { DsdEditorDocumentContentModel } from "./document-view-types";
import {
  ReviewDocumentContentFrame,
  ReviewDocumentEmptyState,
  ReviewDocumentErrorState,
  ReviewDocumentLoadingState,
  ReviewDocumentShell,
} from "./review-document-state";
import { useCloseOnReviewLoadError } from "./use-close-on-review-load-error";
import {
  type DsdEditorMappingReviewInput,
  useDsdEditorMappingReview,
} from "./use-mapping-review";
import {
  getNoteReferenceCandidateStatusByCellId,
  useDsdEditorReviewDocumentData,
} from "./use-review-document-data";
import { deriveDsdReviewStatusByCellId } from "./cell-verdict";
import { pushModal } from "@/stores/modal";
import { isBetaFeatureEnabled } from "@/components/beta-feature";
import { NavigationRail } from "@/toggle-ui-kit/navigation-rail";
import { useDsdEditorReviewScrollRequests } from "./use-review-scroll-requests";
import { useCallback, useEffect, useMemo, useState } from "react";
import { DSD_REVIEW_COPY, DSD_REVIEW_STATUS_LABELS } from "./copy";
import { DsdReviewDocumentView } from "./dsd-review-document-view";
import type { DsdReviewEvidenceCandidate } from "./evidence/evidence-host";
import { useDsdReviewEvidenceStore } from "./evidence/evidence-store";
import { DsdReviewGuideCard } from "./guide-card";
import { DsdReviewMatchingPanel } from "./panel/matching-panel";
import { DsdReviewNoteMatchingPanel } from "./panel/note-matching-panel";
import { buildDsdReviewNoteMatchView } from "./panel/note-match-view";
import { DsdReviewMappingReviewModal } from "./panel/review-modal";
import { tagToneOf } from "./panel/review-card-parts";
import {
  type DsdEditorReviewMode,
  getDsdReviewRailAvailability,
  getDsdReviewRailCounts,
} from "./rail/rail-counts";
import { getDsdReviewRailItems } from "./rail/rail-items";
import { DsdReviewDocumentTitle, DsdReviewHeader } from "./review-header";
import {
  buildDsdReviewSelectedCellView,
  getDsdReviewEvidenceItems,
  isDsdReviewHintTargetCell,
  needsReview,
  type DsdReviewPendingItem,
  getDsdReviewFlaggedSections,
  getDsdReviewMatchChunkIds,
} from "./panel/selected-cell-view";
import { useDsdReviewSelection } from "./use-dsd-review-selection";
import { DsdReviewSumValidationPanel } from "./sum-validation/sum-validation-panel";
import { DsdReviewCorrectionPanel } from "./corrections/correction-panel";
import { useDsdReviewSumValidation } from "./sum-validation/use-dsd-review-sum-validation";
import { useDsdReviewCorrectionNavigation } from "./corrections/use-dsd-review-correction-navigation";
import { getDsdCellAxisLabel } from "@/utils/dsd-editor/cell-label-paths";
import { useDsdReviewCompanyNameReview } from "./company-names/use-dsd-review-company-name-review";
import { DsdReviewCompanyNamePanel } from "./company-names/company-name-panel";
import { logCompanyNameReviewEntry } from "./company-name-review-entry-log";
import { getMatchingModeInputs } from "./matching-mode";
import { keepParagraphValueSegments } from "@/utils/dsd-editor/matched-paragraphs";

export const DsdReviewDocument = ({
  documentId,
  closeOnLoadError,
}: {
  documentId: string;
  closeOnLoadError: boolean;
}) => {
  const data = useDsdEditorReviewDocumentData(documentId);
  // 교정 분류 계약 위반도 data.isError 로 온다 (데이터 훅이 검사)
  const { isError } = data;
  useCloseOnReviewLoadError(isError, closeOnLoadError);
  const mappingReview = useDsdEditorMappingReview(documentId);
  // "검토 필요 항목만 보기" — 세션 안에서만
  const [pendingOnly, setPendingOnly] = useState(false);
  const [activeMode, setActiveMode] = useState<DsdEditorReviewMode>(
    "note-reference-review",
  );
  const matchingMode = activeMode === "note-reference-review";
  // 주석·주석매칭 — 본문·주석매칭과 같은 선택·강조·근거 창을 쓰되 매칭 목록만 noteValueMatches 로 바꾼다. 검토(판단 저장)는 없다
  const noteMatchingMode = activeMode === "note-note-matching";
  const {
    valueMatches,
    noteValueMatches,
    noteReferenceStatusByCellId: backendStatusByCellId,
    noteReferenceChecks,
    selectedCellSummaryById,
  } = data;
  // 셀 색은 후보 카드와 저장된 판단에서 정한다 (cell-verdict.ts). 저장 훅이 캐시를 패치하면 여기서 다시 계산된다
  const derivedStatusByCellId = useMemo(
    () =>
      deriveDsdReviewStatusByCellId({
        backendStatusByCellId,
        noteReferenceChecks,
        valueMatches,
        cellSummaryById: selectedCellSummaryById,
      }),
    [
      backendStatusByCellId,
      noteReferenceChecks,
      valueMatches,
      selectedCellSummaryById,
    ],
  );
  // 모드별 매칭 목록·셀 상태·문단 값 표시 — 정책은 matching-mode.ts 한 곳
  const modeInputs = useMemo(
    () =>
      getMatchingModeInputs({
        mode: activeMode,
        valueMatches,
        noteValueMatches,
        backendStatusByCellId,
        derivedStatusByCellId,
      }),
    [
      activeMode,
      valueMatches,
      noteValueMatches,
      backendStatusByCellId,
      derivedStatusByCellId,
    ],
  );
  const activeMatches = modeInputs.matches;
  const selectionData = useMemo(
    () => ({
      valueMatches: activeMatches,
      renderedCellIds: data.renderedCellIds,
      selectedCellSummaryById,
      noteReferenceStatusByCellId: modeInputs.selectableStatusByCellId,
    }),
    [
      activeMatches,
      data.renderedCellIds,
      selectedCellSummaryById,
      modeInputs.selectableStatusByCellId,
    ],
  );
  // 목차 펼침은 상단 바의 토글이 소유한다 (접힌 동안 hover = 본문 위에 띄움, 클릭 = 고정)
  const sidebar = useDocumentSidebarToggle();
  // 셀을 선택하거나 rail 의 현재 모드를 누르면 열린다. 선택을 지워도 닫지 않는다
  const [panelOpen, setPanelOpen] = useState(false);
  // 아무것도 누르기 전엔 rail 의 현재 모드에 테두리·halo 를 둘러 클릭을 유도한다 (셀 강조와 같은 개념)
  const [touched, setTouched] = useState(false);
  // 진입 코치마크. 첫 사용자 입력에 사라지고 다시 뜨지 않는다
  const [showCellHint, setShowCellHint] = useState(true);
  const scroll = useDsdEditorReviewScrollRequests();
  // 근거 창의 후보는 store 가 든다 (구독은 근거 창과 패널만 — ←/→ 에 이 컴포넌트는 다시 렌더되지 않는다)
  const setEvidenceTarget = useDsdReviewEvidenceStore(
    (state) => state.setTarget,
  );
  // 문서를 떠나면 비운다 — 다음 문서에 같은 셀 id 가 있으면 들어오자마자 창이 열린다
  useEffect(() => () => setEvidenceTarget(null), [setEvidenceTarget]);
  // 셀을 고르면 패널과 함께 첫 후보의 근거 창이 열린다 (후보가 없으면 창은 안 뜬다 — 호스트의 evidence 파생에서 걸러짐)
  const onSelected = useCallback(
    (cellId: string) => {
      setPanelOpen(true);
      setTouched(true);
      setEvidenceTarget({ cellId, index: 0 });
    },
    [setEvidenceTarget],
  );
  const selection = useDsdReviewSelection({
    data: selectionData,
    requestCellScroll: scroll.requestCellScroll,
    onSelected,
  });
  // 합계 셀·교정 항목을 고르면 패널이 열린다 (본문·주석매칭의 셀 선택과 같이)
  const openPanel = useCallback(() => {
    setPanelOpen(true);
    setTouched(true);
  }, []);
  const sumReview = useDsdReviewSumValidation({
    enabled: activeMode === "sum-validation",
    displayItems: data.displayItems,
    requestCellScroll: scroll.requestCellScroll,
    onSelect: openPanel,
  });
  const correctionReview = useDsdReviewCorrectionNavigation({
    enabled: activeMode === "corrections",
    items: data.correctionItems,
    requestCorrectionScroll: scroll.requestCorrectionScroll,
    onSelect: openPanel,
  });
  // 본문 기업명을 누르면 패널이 열린다 (패널 안 클릭이면 이미 열려 있다)
  const companyNameReview = useDsdReviewCompanyNameReview({
    enabled: activeMode === "company-name-review",
    references: data.companyNameReferences,
    requestCellScroll: scroll.requestCellScroll,
    onSelect: openPanel,
  });

  const {
    displayItems,
    correctionCount,
    correctionReady,
    correctionPending,
    companyNameReferences,
  } = data;
  const railCounts = useMemo(
    () =>
      getDsdReviewRailCounts({
        displayItems,
        valueMatches,
        noteValueMatches,
        correctionCount,
        correctionReady,
        companyNameReferences,
      }),
    [
      displayItems,
      valueMatches,
      noteValueMatches,
      correctionCount,
      correctionReady,
      companyNameReferences,
    ],
  );
  const railAvailability = useMemo(
    () =>
      getDsdReviewRailAvailability({
        displayItems,
        noteValueMatches,
        correctionCount,
        correctionReady,
        companyNameReferences,
      }),
    [
      displayItems,
      noteValueMatches,
      correctionCount,
      correctionReady,
      companyNameReferences,
    ],
  );
  const railItems = useMemo(
    () =>
      getDsdReviewRailItems({
        counts: railCounts,
        availability: railAvailability,
        correctionPending,
      }),
    [railCounts, railAvailability, correctionPending],
  );

  // 사이드바 토글 등 이 컴포넌트의 재렌더에서 문서 콘텐츠가 같은 객체를 받도록 고정
  const { sections, matchedParagraphSegments, renderedCellIds } = data;
  // 주석·주석매칭에서는 비어 있다 — 본문 셀은 무색, 후보 셀은 상태 없는 source 를 물려받아 전부 초록
  const noteReferenceStatusByCellId = modeInputs.colorStatusByCellId;
  const noteReferenceCandidateStatusByCellId = useMemo(
    () =>
      getNoteReferenceCandidateStatusByCellId(
        activeMatches,
        noteReferenceStatusByCellId,
        renderedCellIds,
      ),
    [activeMatches, noteReferenceStatusByCellId, renderedCellIds],
  );
  const documentModel = useMemo(
    () => ({ sections, items: displayItems }),
    [sections, displayItems],
  );
  const {
    linkedCellIds,
    candidateCellIds,
    selectedCellId,
    previewCellId,
    selectCell,
    clearSelection,
    canSelectCell,
  } = selection;
  // 문단 값도 셀과 같은 선택 규칙 — matchedParagraphs 에는 두 매칭 모드의 문단이 섞여 온다
  const { showsParagraphValues } = modeInputs;
  const paragraphSegments = useMemo(
    () =>
      showsParagraphValues
        ? keepParagraphValueSegments(matchedParagraphSegments, canSelectCell)
        : undefined,
    [showsParagraphValues, matchedParagraphSegments, canSelectCell],
  );
  // 검토 완료 처리(검토 모달 진입)는 베타 — 근거 창 쪽은 여기 한 곳에서 끈다
  const reviewEnabled = isBetaFeatureEnabled();
  const noteReferenceReview = useMemo<
    NonNullable<DsdEditorDocumentContentModel["noteReferenceReview"]>
  >(
    () => ({
      linkedCellIds,
      candidateCellIds,
      statusByCellId: noteReferenceStatusByCellId,
      // 토글(베타 포함)을 켠 동안만, 본문·주석매칭에서만 — 주석·주석매칭은 본문 셀 상태가 없어 하드룰도 없다
      hardRuleCountByCellId:
        data.hardRuleHighlight && !noteMatchingMode
          ? data.hardRuleCountByCellId
          : undefined,
      statusLabels: DSD_REVIEW_STATUS_LABELS,
      candidateStatusByCellId: noteReferenceCandidateStatusByCellId,
      // 패널 후보 카드 호버(미리보기)가 선택보다 우선 — 카드는 셀이 선택된 뒤에만 있으므로 반대면 미리보기가 죽는다
      focusCellId: previewCellId ?? selectedCellId,
      matchedParagraphSegments: paragraphSegments,
    }),
    [
      linkedCellIds,
      candidateCellIds,
      noteReferenceStatusByCellId,
      data.hardRuleHighlight,
      noteMatchingMode,
      data.hardRuleCountByCellId,
      noteReferenceCandidateStatusByCellId,
      selectedCellId,
      previewCellId,
      paragraphSegments,
    ],
  );
  // 다른 모드에서는 일반 셀 클릭을 받지 않고, 빈 곳 클릭은 그 모드의 선택을 지운다
  const { clear: clearSumSelection } = sumReview;
  const { clear: clearCorrection } = correctionReview;
  const { leaveDetail: leaveCompanyDetail } = companyNameReview;
  const modelSelection = useMemo(() => {
    if (activeMode === "sum-validation")
      return { selectedCellId: null, onClearSelection: clearSumSelection };
    if (activeMode === "corrections")
      return { selectedCellId: null, onClearSelection: clearCorrection };
    // 빈 곳 클릭 → 선택 해제 + 대표명 목록으로
    if (activeMode === "company-name-review")
      return { selectedCellId: null, onClearSelection: leaveCompanyDetail };
    return {
      selectedCellId,
      onCellSelect: selectCell,
      onClearSelection: clearSelection,
      canSelectCell,
    };
  }, [
    activeMode,
    clearSumSelection,
    clearCorrection,
    leaveCompanyDetail,
    selectedCellId,
    selectCell,
    clearSelection,
    canSelectCell,
  ]);
  const sumValidationModel = useMemo(
    () => ({
      enabled: activeMode === "sum-validation",
      selectedCellId: sumReview.selectedCellId,
      highlightedCellIds: sumReview.highlightedCellIds,
      onSelectCell: sumReview.select,
      cells: sumReview.cells,
    }),
    [
      activeMode,
      sumReview.selectedCellId,
      sumReview.highlightedCellIds,
      sumReview.select,
      sumReview.cells,
    ],
  );
  // 선택한 합계 셀의 "행 | 열" — 표기 경로 계약은 데이터 훅이 불러올 때 검사했다 (위반이면 data.isError)
  const sumAxisLabel = useMemo(
    () =>
      sumReview.selectedCellId
        ? getDsdCellAxisLabel(displayItems, sumReview.selectedCellId)
        : "",
    [displayItems, sumReview.selectedCellId],
  );
  const { select: selectCorrection } = correctionReview;
  const correctionModel = useMemo(
    () => ({
      enabled: activeMode === "corrections",
      activeItemId: correctionReview.activeItemId,
      correctionBlockByItemId: data.correctionBlockByItemId,
      correctionBlockByCellId: data.correctionBlockByCellId,
      // 본문에서 교정을 누르면 이미 그 자리라 스크롤 없이 선택만 (패널은 훅의 onSelect 가 연다)
      onSelect: selectCorrection,
    }),
    [
      activeMode,
      correctionReview.activeItemId,
      data.correctionBlockByItemId,
      data.correctionBlockByCellId,
      selectCorrection,
    ],
  );
  const companyNameModel = useMemo(
    () => ({
      enabled: activeMode === "company-name-review",
      paragraphSegments: data.companyNameParagraphSegments,
      cellSegmentsByCellId: data.companyNameCellSegments,
      cautionOccurrenceIds: data.companyNameCautionOccurrenceIds,
      selectedOccurrenceId: companyNameReview.selectedOccurrenceId,
      // 본문에서 누르면 이미 그 자리라 스크롤 없이 그 대표명 상세로
      onSelectOccurrence: companyNameReview.selectOccurrenceFromDocument,
    }),
    [
      activeMode,
      data.companyNameParagraphSegments,
      data.companyNameCellSegments,
      data.companyNameCautionOccurrenceIds,
      companyNameReview.selectedOccurrenceId,
      companyNameReview.selectOccurrenceFromDocument,
    ],
  );
  const matchChunkIds = useMemo(
    () => getDsdReviewMatchChunkIds(activeMatches),
    [activeMatches],
  );
  // 목차 점 배지 — 지금 모드에서 볼 것이 있는 섹션
  const flaggedSections = useMemo(() => {
    if (activeMode === "sum-validation")
      return new Set(sumReview.mismatchCells.map((cell) => cell.sectionName));
    if (activeMode === "corrections")
      return new Set(data.correctionItems.map((item) => item.sectionName));
    if (activeMode === "company-name-review")
      return new Set(
        displayItems
          .filter((item) => data.companyNameCautionChunkIds.has(item.chunkId))
          .map((item) => item.sectionName),
      );
    return getDsdReviewFlaggedSections(
      displayItems,
      canSelectCell,
      matchChunkIds,
    );
  }, [
    activeMode,
    sumReview.mismatchCells,
    data.correctionItems,
    data.companyNameCautionChunkIds,
    displayItems,
    canSelectCell,
    matchChunkIds,
  ]);
  // 본문·주석매칭의 패널 모델. 주석·주석매칭에서는 null — 그 모드의 검토·근거 콜백이 전부 빈 목록 위에서 무동작이 된다
  const selectedCellView = useMemo(
    () =>
      noteMatchingMode
        ? null
        : buildDsdReviewSelectedCellView({
            selectedCellId,
            cellSummaryById: data.selectedCellSummaryById,
            activeValueMatch: selection.activeValueMatch,
            // 문구는 백엔드 상태, 배너 톤은 검토 결과로 파생한 색
            statusByCellId: backendStatusByCellId,
            colorStatusByCellId: noteReferenceStatusByCellId,
            noteReferenceChecks,
          }),
    [
      noteMatchingMode,
      selectedCellId,
      data.selectedCellSummaryById,
      selection.activeValueMatch,
      backendStatusByCellId,
      noteReferenceStatusByCellId,
      noteReferenceChecks,
    ],
  );
  const noteMatchView = useMemo(
    () =>
      noteMatchingMode
        ? buildDsdReviewNoteMatchView({
            selectedCellId,
            cellSummaryById: data.selectedCellSummaryById,
            activeValueMatch: selection.activeValueMatch,
          })
        : null,
    [
      noteMatchingMode,
      selectedCellId,
      data.selectedCellSummaryById,
      selection.activeValueMatch,
    ],
  );

  const isHintTargetCell = useCallback(
    (cellId: string) =>
      isDsdReviewHintTargetCell(
        cellId,
        noteReferenceStatusByCellId,
        candidateCellIds,
      ),
    [noteReferenceStatusByCellId, candidateCellIds],
  );
  // 근거 창이 도는 후보 — "검토 필요 항목만 보기" 면 패널과 같이 미검토만
  const evidenceItems = useMemo(
    () => getDsdReviewEvidenceItems(selectedCellView?.items ?? [], pendingOnly),
    [selectedCellView, pendingOnly],
  );
  // 토글로 목록이 바뀌면 보던 후보를 새 목록에서 다시 찾고, 없으면 첫 후보로 (미검토가 없으면 창을 닫는다)
  const onPendingOnlyChange = useCallback(
    (next: boolean) => {
      setPendingOnly(next);
      const target = useDsdReviewEvidenceStore.getState().target;
      if (
        !target ||
        !selectedCellView ||
        target.cellId !== selectedCellView.cellId
      )
        return;
      const current = evidenceItems[target.index];
      const nextItems = getDsdReviewEvidenceItems(selectedCellView.items, next);
      const index = Math.max(0, nextItems.indexOf(current));
      setEvidenceTarget(
        nextItems.length > 0 ? { cellId: target.cellId, index } : null,
      );
    },
    [selectedCellView, evidenceItems, setEvidenceTarget],
  );
  // 저장 뒤 근거 창 정리 — 패널 원형·근거 창 버튼 어느 쪽에서 저장하든 여기 한 곳.
  // "검토 필요만 보기" 면 저장한 후보가 목록에서 빠진다: 그 자리의 다음 후보로, 없으면 닫는다.
  // (store 의 index 를 그대로 두면 창이 소리 없이 다른 후보를 가리키거나, 닫힌 채 index 가 남아 필터를 끌 때 다시 열린다)
  const { save: saveMappingReview } = mappingReview;
  const saveReview = useCallback(
    async (input: DsdEditorMappingReviewInput) => {
      await saveMappingReview(input);
      const target = useDsdReviewEvidenceStore.getState().target;
      if (!pendingOnly || !target || target.cellId !== selectedCellId) return;
      const savedIndex = evidenceItems.findIndex(
        (item) => item.target === input.target,
      );
      if (savedIndex < 0) return;
      const remaining = evidenceItems.length - 1;
      if (remaining === 0) {
        setEvidenceTarget(null);
        return;
      }
      setEvidenceTarget({
        cellId: target.cellId,
        index:
          target.index > savedIndex
            ? target.index - 1
            : Math.min(target.index, remaining - 1),
      });
    },
    [
      saveMappingReview,
      pendingOnly,
      selectedCellId,
      evidenceItems,
      setEvidenceTarget,
    ],
  );
  // 패널(memo)에 넘기는 콜백 — 참조가 흔들리면 memo 가 무의미하다
  const bannerText = selectedCellView?.banner?.text ?? null;
  const onReview = useCallback(
    (item: DsdReviewPendingItem) =>
      pushModal(DsdReviewMappingReviewModal, {
        item,
        bannerText: item.hasEvidence
          ? bannerText
          : DSD_REVIEW_COPY.panel.noEvidenceBanner[
              item.tag === "unreferenced" ? "unreferenced" : "referenced"
            ],
        onSave: saveReview,
      }),
    [bannerText, saveReview],
  );
  // 근거 창에 넘기는 후보 — 카드 모델에서 창이 읽는 것만. 미검토 후보에는 패널 원형과 같은 검토 모달을 여는 onReview
  const evidenceCandidates = useMemo<DsdReviewEvidenceCandidate[]>(
    () =>
      noteMatchView
        ? // 주석·주석매칭: 검토 없음(onReview null), 늘 초록·금액 일치
          noteMatchView.items.map((item) => ({
            occurrence: item.occurrence,
            tone: "positive",
            hasEvidence: true,
            summary: {
              tagLabel: item.tagLabel,
              tagTone: "positive",
              matchLabel: item.matchLabel,
              matchPositive: true,
              judgement: null,
            },
            onReview: null,
          }))
        : evidenceItems.map((item) => ({
            occurrence: item.occurrence,
            tone: item.tone,
            hasEvidence: item.hasEvidence,
            summary: {
              tagLabel: DSD_REVIEW_COPY.panel.tag[item.tag],
              tagTone: tagToneOf(item),
              matchLabel: item.matchLabel,
              matchPositive: item.matchTone === "positive",
              judgement: item.review?.judgement ?? null,
            },
            // 미검토면 검토, 판단이 있으면 수정 — 둘 다 같은 모달. 검토 완료 처리는 베타라 끄면 null (근거 창의 버튼·Enter 가 함께 없다)
            onReview:
              reviewEnabled && needsReview(item) ? () => onReview(item) : null,
          })),
    [noteMatchView, evidenceItems, onReview, reviewEnabled],
  );
  // 선택은 본문 셀에 둔 채 그 아래 근거 창에 후보의 주석을 띄운다
  // 셀을 클릭했을 때와 같은 한 번의 스크롤(30% + 창 자리) 뒤 창이 뜬다
  const { scrollToCell } = selection;
  const onShowEvidence = useCallback(
    (occurrence: DsdEditorValueOccurrence) => {
      if (!selectedCellId) return;
      const index = evidenceCandidates.findIndex(
        (item) => item.occurrence === occurrence,
      );
      if (index < 0) return;
      scrollToCell(selectedCellId);
      setEvidenceTarget({ cellId: selectedCellId, index });
    },
    [selectedCellId, evidenceCandidates, scrollToCell, setEvidenceTarget],
  );

  // 문서가 준비되지 않은 분기(에러·로딩·빈 문서)에는 목차 사이드바가 없으므로 토글도 막는다
  const documentReady = !isError && !data.isLoading && !data.isEmpty;
  const header = (
    <DsdReviewHeader
      sidebar={sidebar.headerToggle}
      sidebarDisabled={!documentReady}
      unitDifferenceV2={data.unitDifferenceV2}
      onUnitDifferenceV2Change={data.setUnitDifferenceV2}
      hardRuleHighlight={data.hardRuleHighlight}
      onHardRuleHighlightChange={data.setHardRuleHighlight}
    />
  );

  if (isError) {
    return (
      <ReviewDocumentShell>
        <AppPageShell header={header}>
          <ReviewDocumentErrorState
            message={data.errorMessage}
            onRetry={() => data.retry()}
          />
        </AppPageShell>
      </ReviewDocumentShell>
    );
  }

  if (data.isLoading) {
    return (
      <ReviewDocumentShell>
        <AppPageShell header={header}>
          <ReviewDocumentLoadingState />
        </AppPageShell>
      </ReviewDocumentShell>
    );
  }

  if (data.isEmpty) {
    return (
      <ReviewDocumentShell>
        <AppPageShell header={header}>
          <ReviewDocumentEmptyState />
        </AppPageShell>
      </ReviewDocumentShell>
    );
  }

  // year 0 = 업로드 시 파싱 실패
  const metadata = data.report?.metadata;
  const sidebarPeriodInfo = metadata && {
    reportType: metadata.reportBase,
    year: metadata.year > 0 ? String(metadata.year) : "",
    period: metadata.quarter,
  };

  const rail = (
    <div className="relative flex shrink-0">
      {!panelOpen && (
        <DsdReviewGuideCard
          mode={activeMode}
          count={railCounts[activeMode]}
          variant="floating"
          // 위쪽 경계가 파일명 줄(48px)의 중간에 오도록 (Figma 7647-490523)
          className="absolute right-full top-6 z-20 mr-3"
        />
      )}
      <NavigationRail
        ariaLabel={DSD_REVIEW_COPY.rail.ariaLabel}
        items={railItems}
        activeId={activeMode}
        emphasizeActive={!touched}
        onSelect={(mode) => {
          setTouched(true);
          // 현재 모드를 다시 누르면 패널을 여닫는다 (셀 선택 없이도 안내를 패널로)
          if (mode === activeMode) {
            setPanelOpen((open) => !open);
            return;
          }
          // 모드를 바꾸면 매칭 선택·근거 창을 닫는다. 합계·교정 선택은 각 훅이 모드를 벗어날 때 지운다
          clearSelection();
          setEvidenceTarget(null);
          logCompanyNameReviewEntry({
            nextMode: mode,
            currentMode: activeMode,
            showOverview: false,
            entry: "툴바",
            file: {
              filename: data.report?.filename ?? "",
              dsdFileId: documentId,
            },
          });
          setActiveMode(mode);
          setPanelOpen(true);
        }}
      />
    </div>
  );

  return (
    <ReviewDocumentShell>
      <AppPageShell header={header}>
        <ReviewDocumentContentFrame>
          <DsdReviewDocumentView
            model={{
              document: documentModel,
              noteReferenceReview,
              selection: modelSelection,
              sumValidationReview: sumValidationModel,
              correctionReview: correctionModel,
              companyNameReview: companyNameModel,
              scrollRequest: scroll.scrollRequest,
            }}
            sidebar={sidebar}
            periodInfo={sidebarPeriodInfo}
            flaggedSections={flaggedSections}
            documentHeader={
              <DsdReviewDocumentTitle filename={data.report?.filename} />
            }
            panelOpen={panelOpen}
            showCellHint={
              matchingMode &&
              showCellHint &&
              noteReferenceStatusByCellId.size > 0
            }
            onCellHintDismiss={() => setShowCellHint(false)}
            evidenceItems={evidenceCandidates}
            onScrollToSelectedCell={selection.scrollToSelectedCell}
            scrollTargetCellId={selection.scrollTargetCellId}
            isHintTargetCell={isHintTargetCell}
            panel={
              activeMode === "sum-validation" ? (
                <DsdReviewSumValidationPanel
                  count={railCounts["sum-validation"]}
                  selected={sumReview.selectedCell}
                  axisLabel={sumAxisLabel}
                  mismatchCells={sumReview.mismatchCells}
                  onScrollToCell={() => {
                    if (sumReview.selectedCellId)
                      scroll.requestCellScroll(sumReview.selectedCellId);
                  }}
                  onPrevious={() => sumReview.navigate(-1)}
                  onNext={() => sumReview.navigate(1)}
                />
              ) : activeMode === "corrections" ? (
                <DsdReviewCorrectionPanel
                  count={railCounts.corrections}
                  items={data.correctionItems}
                  activeItemId={correctionReview.activeItemId}
                  onSelect={correctionReview.selectAndScroll}
                  onClear={correctionReview.clear}
                  onPrevious={() => correctionReview.navigate(-1)}
                  onNext={() => correctionReview.navigate(1)}
                />
              ) : noteMatchingMode ? (
                <DsdReviewNoteMatchingPanel
                  count={railCounts["note-note-matching"]}
                  selected={noteMatchView}
                  onScrollToCell={selection.scrollToSelectedCell}
                  onPreviewOccurrence={selection.previewOccurrence}
                  onShowEvidence={onShowEvidence}
                />
              ) : activeMode === "company-name-review" ? (
                <DsdReviewCompanyNamePanel
                  count={railCounts["company-name-review"]}
                  references={data.companyNameReferences}
                  locationByOccurrenceId={
                    data.companyNameLocationByOccurrenceId
                  }
                  selectedRepresentative={
                    companyNameReview.selectedRepresentative
                  }
                  selectedOccurrenceId={companyNameReview.selectedOccurrenceId}
                  needsConfirmationOnly={
                    companyNameReview.needsConfirmationOnly
                  }
                  onNeedsConfirmationOnlyChange={
                    companyNameReview.setNeedsConfirmationOnly
                  }
                  onSelectRepresentative={
                    companyNameReview.selectRepresentative
                  }
                  onSelectOccurrence={(occurrence) =>
                    companyNameReview.selectOccurrence(occurrence.occurrenceId)
                  }
                  onBack={companyNameReview.leaveDetail}
                />
              ) : (
                <DsdReviewMatchingPanel
                  showHardRule={data.hardRuleHighlight}
                  count={railCounts["note-reference-review"]}
                  hasChecks={
                    railCounts["note-reference-review"] > 0 ||
                    data.noteReferenceChecks.length > 0
                  }
                  selected={selectedCellView}
                  onScrollToCell={selection.scrollToSelectedCell}
                  onPreviewOccurrence={selection.previewOccurrence}
                  onReview={onReview}
                  pendingOnly={pendingOnly}
                  onPendingOnlyChange={onPendingOnlyChange}
                  onShowEvidence={onShowEvidence}
                />
              )
            }
            rail={rail}
          />
        </ReviewDocumentContentFrame>
      </AppPageShell>
    </ReviewDocumentShell>
  );
};
